import React, { useState } from 'react';
import styled from 'styled-components';
import { PasswordFieldProps, PasswordRules, PasswordRuleError, PasswordErrorReporter } from '../models';
import TextField from './TextField';
import DynamicLink from 'packages/smart-home-support/components/DynamicLink';
import { LinkId } from 'packages/smart-home-support/constants';

const s = {
  ShowPasswordButton: styled.button`
    cursor: pointer;
    color: #636363;
    font-size: 14px;
    background: none;
    border: none;
    padding-top: 0;
    padding-right: 13px;
    padding-left: 10px;
    padding-bottom: 0;
  `,
  ForgotPasswordLink: styled(DynamicLink)`
    color: #1b1b1b;
    text-decoration: none;
    font-size: 12px;
    text-align: right;
    position: absolute;
    top: 10px;
    right: 0;
    padding-bottom: 5px;
    display: block;
  `,
};

export const defaultRules: PasswordRules = [
  (pw) => pw.length <= 7 && PasswordRuleError.TooShort,
  (pw) => pw.length >= 256 && PasswordRuleError.TooLong,
  (pw) => !pw.match(/[a-z]/i) && PasswordRuleError.NeedsLetter,
  (pw) => !pw.match(/\d/) && PasswordRuleError.NeedsNumber,
  (pw) =>
    // Matches the following characters: ^$*.[]{}()?-"!@#%&/\\,><\':;|_~`
    !pw.match(/[\^\$\*\.\[\]{}\(\)\?\-"!@#%&\/\\,><':;\|_~`]/) && PasswordRuleError.NeedsSpecialCharacter,
];

const defaultErrorReporter: PasswordErrorReporter = (errors) => {
  const makeError = (msg: string) => `Password must ${msg}.`;
  const errorMap: Record<PasswordRuleError, string> = {
    [PasswordRuleError.TooShort]: 'be 7+ characters',
    [PasswordRuleError.TooLong]: 'be shorter than 256 characters',
    [PasswordRuleError.NeedsLetter]: 'have at least one letter',
    [PasswordRuleError.NeedsNumber]: 'have at least one number',
    [PasswordRuleError.NeedsSpecialCharacter]: 'have at least one special character',
  };
  const errorStrings = errors.map((e) => errorMap[e]);

  if (errorStrings.length === 1) {
    return makeError(errorStrings[0]);
  } else if (errorStrings.length === 2) {
    return makeError(`${errorStrings[0]} and ${errorStrings[1]}`);
  } else if (errorStrings.length) {
    const last = errorStrings.pop() as string;
    return makeError(`${errorStrings.join(', ')}, and ${last}`);
  }
  return false;
};

const getPasswordRulesError = (value: string, rules: PasswordRules, reporter: PasswordErrorReporter) => {
  const errors = rules.map((rule) => rule(value)).filter(Boolean);
  return reporter(errors as Array<PasswordRuleError>);
};

const PasswordField: React.FC<PasswordFieldProps> = (props) => {
  const [isHidden, setIsHidden] = useState(true);
  const {
    hasShowPasswordButton = false,
    forgotPasswordLabel = 'Forgot password?',
    errorReporter = defaultErrorReporter,
    rules = defaultRules,
    toForgotPassword = '',
    getFieldError = (value: string) => getPasswordRulesError(value, rules, errorReporter),
    onForgotPasswordClick,
  } = props;
  const inputWidget = hasShowPasswordButton ? (
    <s.ShowPasswordButton type="button" tabIndex={-1} onClick={() => setIsHidden(!isHidden)}>
      {isHidden ? 'Show' : 'Hide'}
    </s.ShowPasswordButton>
  ) : (
    props.inputWidget
  );

  return (
    <>
      <div style={{ position: 'relative' }}>
        {(onForgotPasswordClick || toForgotPassword) && (
          <s.ForgotPasswordLink id={LinkId.AuthForgotPassword} to={toForgotPassword} onClick={onForgotPasswordClick}>
            {forgotPasswordLabel}
          </s.ForgotPasswordLink>
        )}
        <TextField
          name="password"
          {...props}
          type={isHidden ? 'password' : 'text'}
          inputWidget={inputWidget}
          getFieldError={getFieldError}
        />
      </div>
    </>
  );
};

export default PasswordField;



// WEBPACK FOOTER //
// ./packages/smart-home-support/components/forms/fields/PasswordField.tsx